import React from 'react';
import { useTranslation } from "react-i18next";
import { FormField as FormFieldType, FormData, FormFiles, FormErrors } from '../../../../../types/ContactForm';

interface FileFieldProps {
    field: FormFieldType;
    formData: FormData;
    files: FormFiles;
    errors: FormErrors;
    inputCssClass?: string;
    onFileChange: (name: string, file: File | null) => void;
    onFieldFocus: (fieldName: string) => void;
}

export const FileField: React.FC<FileFieldProps> = ({
    field,
    files,
    errors,
    inputCssClass,
    onFileChange,
    onFieldFocus
}) => {
    const handleFileChange = (file: File | null) => {
        onFileChange(field.name, file);
        onFieldFocus(field.name);
    };

    const fileMaxSize = field.maxFileSize || 5; // MB
    const currentFile = files[field.name];
    const fileSizeError = currentFile && currentFile.size > (fileMaxSize * 1024 * 1024);
    const fileSizeMB = currentFile ? (currentFile.size / (1024 * 1024)).toFixed(2) : 0;

    const getAcceptAttribute = () => {
        if (!field.allowedFileTypes || field.allowedFileTypes.trim() === '') {
            return ".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png";
        }

        const extensions = field.allowedFileTypes
            .split(',')
            .map(ext => ext.trim())
            .filter(ext => ext)
            .map(ext => ext.startsWith('.') ? ext : `.${ext}`);

        return extensions.join(',');
    };

    const { t } = useTranslation('form');

    return (
        <div className={`col-12 col-md-${field.width || 12}`}>
            <div
                className={`form-group ${field.required ? 'required' : ''} ${errors[field.name] || fileSizeError ? 'has-error' : ''}`}>
                <label htmlFor={`field-${field.id}`} className="control-label">
                    {field.label}
                </label>
                <input
                    type="file"
                    id={`field-${field.id}`}
                    name={field.name}
                    className="form-control"
                    accept={getAcceptAttribute()}
                    onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
                    required={field.required}
                    aria-required={field.required}
                />

                {currentFile && (
                    <div className="form-text mt-1">
                        <div
                            className={`d-flex align-items-center ${fileSizeError ? 'text-danger' : 'text-muted'}`}>
                            <i className="fas fa-file me-1"></i>
                            <span>{currentFile.name} ({fileSizeMB}MB)</span>
                            {fileSizeError && (
                                <i className="fa fa-exclamation-triangle ms-2" title="Datei zu groß"></i>
                            )}
                        </div>
                    </div>
                )}

                {fileSizeError && (
                    <div className="form-error-msg text-danger">
                        <i className="fa fa-warning"></i> {(t('maxSize', { fileMaxSize }))}
                    </div>
                )}

                <small className="form-text text-muted">
                    {field.allowedFileTypes && field.allowedFileTypes.trim() !== ''
                        ? t('allowedFileTypes', { types: field.allowedFileTypes, fileMaxSize })
                        : t('allowedFiles', { fileMaxSize })
                    }
                </small>

                {errors[field.name] && !fileSizeError && (
                    <div className="form-error-msg text-danger">
                        <i className="fa fa-warning"></i> {errors[field.name]}
                    </div>
                )}
            </div>
        </div>
    );
};

